import React from 'react';
import { Spinner } from '../../components/ui';
import ExportButton from '../../components/ExportButton';

// Phone Brief Report: the headline numbers as tiles, then one card per part —
// a phone cannot hold the wide tables, so each line is read down instead of
// across, and the PDF / Excel carry the full tables. Logic: useBrief.
export default function BriefMobile({ vm }) {
  const {
    canView, loading, blocks, toggleBlock, blockLabels, twoSided,
    shownFunctions, memberRows, memberTotals, toggleFunction, pastFunctions,
    invitedByRows, invitedByTotals,
    cardStats,
    moneyRows, moneyTotals, toggleCategory, withEstimates, setWithEstimates,
    summary, report, money, reload,
  } = vm;

  if (!canView) return <div className="card card-pad empty">The brief covers the whole event, so only an Event Admin can open it.</div>;

  const Head = ({ k, sub }) => (
    <div className="brief-head">
      <label className="brief-tick">
        <input type="checkbox" checked={blocks[k]} onChange={() => toggleBlock(k)} />
        <strong>{blockLabels[k]}</strong>
      </label>
      {sub && <div className="muted" style={{ fontSize: 12 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="brief-page brief-mobile">
      <div className="brief-bar">
        <ExportButton report={report} label="⇪ Export brief" className="btn primary sm" />
        <button className="btn light sm" onClick={reload}>↻</button>
      </div>

      <div className="mn-tiles m">
        {summary.map(s => <div key={s.label} className="mn-tile"><span>{s.label}</span><strong>{s.value}</strong></div>)}
      </div>

      {loading ? <Spinner /> : (
        <>
          <div className="card card-pad brief-block">
            <Head k="members" sub={`${memberTotals.persons} persons over the ticked functions${pastFunctions.length ? ` · ${pastFunctions.length} over, left out` : ''}`} />
            {blocks.members && memberRows.map(r => (
              <div key={r.key} className={`brief-line${r.on ? '' : ' brief-off'}`}>
                <label className="brief-tick">
                  <input type="checkbox" checked={r.on} onChange={() => toggleFunction(r.FunctionId)} />
                  <strong>{r.FunctionName}</strong>
                </label>
                <div className="muted" style={{ fontSize: 12 }}>{r.date}{r.time ? ` · ${r.time}` : ''}</div>
                <div style={{ fontSize: 13 }}>
                  <strong>{r.persons}</strong> persons · {r.parties} parties
                  {r.extra ? ` · ${r.extra} on extra cards` : ''}
                  {twoSided && (r.groom || r.bride) ? ` · ${r.groom} groom / ${r.bride} bride` : ''}
                </div>
              </div>
            ))}
            {blocks.members && memberRows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No functions yet.</div>}
          </div>

          <div className="card card-pad brief-block">
            <Head k="invitedBy" sub={`${invitedByRows.length} invited ${invitedByTotals.persons} persons in all`} />
            {blocks.invitedBy && invitedByRows.map(r => (
              <div key={r.key} className="brief-line">
                <strong>{r.name}</strong>{r.isGuest && <span className="chip" style={{ marginLeft: 6 }}>Guest</span>}
                <div className="muted" style={{ fontSize: 12 }}>{r.mobile || '—'}</div>
                <div style={{ fontSize: 13 }}>
                  <strong>{r.persons}</strong> persons · {r.guests} guests · {r.totalCards} cards
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {shownFunctions.map(f => `${f.FunctionName} ${r.perFn[String(f.FunctionId)] || 0}`).join(' · ')}
                </div>
              </div>
            ))}
            {blocks.invitedBy && invitedByRows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No guests yet.</div>}
          </div>

          <div className="card card-pad brief-block">
            <Head k="cards" sub={`${cardStats.households} households · ${cardStats.toOrder} to order`} />
            {blocks.cards && (
              <>
                {cardStats.rows.map(r => (
                  <div key={r.key} className="brief-line">
                    <strong>{r.name}</strong> <span className="muted" style={{ fontSize: 12 }}>{r.kind}</span>
                    <div style={{ fontSize: 13 }}>
                      <strong>{r.total}</strong> to order · {r.households} households{r.extra ? ` + ${r.extra} extra` : ''}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>{r.forFunctions || '—'}</div>
                  </div>
                ))}
                {cardStats.rows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No card types yet — set them up on the Cards page.</div>}
                {(cardStats.extraCards || cardStats.ownFamily || cardStats.withoutType) ? (
                  <div className="muted" style={{ fontSize: 12 }}>
                    {cardStats.extraCards ? `${cardStats.extraCards} extra cards (${cardStats.extraPersons} persons) · ` : ''}
                    {cardStats.ownFamily ? `${cardStats.ownFamily} own family — no card · ` : ''}
                    {cardStats.withoutType ? `${cardStats.withoutType} households with no card type` : ''}
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="card card-pad brief-block">
            <Head k="money" sub={`${money(moneyTotals.paid)} paid · ${money(moneyTotals.balance)} to pay`} />
            {blocks.money && (
              <>
                <label className="brief-tick" style={{ fontSize: 12.5 }}>
                  <input type="checkbox" checked={withEstimates} onChange={e => setWithEstimates(e.target.checked)} />
                  Count amounts still marked “estimate”
                </label>
                {moneyRows.map(r => (
                  <div key={r.key} className={`brief-line${r.on ? '' : ' brief-off'}`}>
                    <label className="brief-tick">
                      <input type="checkbox" checked={r.on} onChange={() => toggleCategory(r.key)} />
                      <strong>{r.category}</strong>
                    </label>
                    <div style={{ fontSize: 13 }}>
                      {money(r.total)} · paid {money(r.paid)} · <strong>{money(r.balance)}</strong> to pay
                      {r.estimates ? ` · ${r.estimates} est.` : ''}
                    </div>
                  </div>
                ))}
                {moneyRows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>Nothing booked yet.</div>}
                <div className="brief-line">
                  <strong>Total{moneyTotals.estimates ? ' (incl. estimates)' : ''}</strong>
                  <div style={{ fontSize: 13 }}>
                    {money(moneyTotals.total)} · paid {money(moneyTotals.paid)} · <strong>{money(moneyTotals.balance)}</strong> to pay
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
